import React, {useState} from 'react';
import {useLocation} from "react-router-dom";
import qs from 'query-string';
import Switch from 'react-switch';
import Select from 'react-select';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import { useSystemMessage } from '../../hooks/useSystemMessage';
import useLoading from '../../hooks/useLoading';
import useAppRouter from "../../hooks/useAppRouter";

import { roomApi } from '../../apis/admin/room/roomApi';
import { roomSpaceMasterApi } from '../../apis/admin/room/roomSpaceMasterApi';
import { roomVhDefMasterApi } from '../../apis/admin/room/roomVhDefMasterApi';
import { roomMenusetApi } from '../../apis/admin/room/roomMenusetApi';

import { SelectOwnerModal } from '../../components/pages/admin/RoomEdit/SelectOwnerModal';
import { SelectSalesmanModal } from '../../components/pages/admin/RoomEdit/SelectSalesmanModal';

import {Member, Salesman, SelectOption} from '../../types/admin/room';
import {StepNav} from "../../components/pages/admin/RoomCreate/StepNav";
import {FormValidationType, makeRegisterOptions} from "../../libs/reactHookForm/makeRegisterOptions";
import {ValidationErrorMessages} from "../../components/pages/common/ValidationErrorMessages";
import {ApiClientError} from "../../apis/errors";
import {config} from "../../libs/config/config";
import {SecondarySalesmanList} from "../../components/pages/admin/RoomCreate/SecondarySalesmanList";
import {logError} from "../../libs/utils/logError";

export const RoomCreatePage: React.FC = () => {
  const TOTAL_STEP_COUNT = 5;

  const { goto } = useAppRouter();
  const { success, warn } = useSystemMessage();
  const withLoading = useLoading();
  const location = useLocation();

  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    setValue,
    getValues,
  } = useForm({
    reValidateMode: 'onSubmit',
    shouldUnregister: false,
  });

  const {
    append: secondarySalesmanAppend,
    remove: secondarySalesmanRemove,
  } = useFieldArray({
    control,
    name: 'secondarySalesmans',
  })

  const [currentStep, setCurrentStep] = React.useState<number>(1);

  const [vhSpaceSetList, setVHSpaceSetLists] = React.useState<SelectOption[]>([]);
  const [vhDefinesList, setVHDefinesLists] = React.useState<SelectOption[]>([]);
  const [menusetList, setMenusetLists] = React.useState<SelectOption[]>([]);

  const [showSelectOwnerModal, setShowSelectOwnerModal] = React.useState<boolean>(false);
  const [showSelectPrimarySalesmanModal, setShowSelectPrimarySalesmanModal] = React.useState<boolean>(false);
  const [showSelectSecondarySalesmanModal, setShowSelectSecondarySalesmanModal] = React.useState<boolean>(false);

  const [ownerName, setOwnerName] = React.useState<string>();
  const [primarySalesman, setPrimarySalesman] = React.useState<Salesman>();
  const [selectedSalesmanEmail, setSelectedSalesmanEmails] = React.useState<string[]>([]);

  const [ apiClientError, setApiClientError ] = useState<ApiClientError | null>(null);

  const loadRoomSpaceMaster = async () => {
    const result = await roomSpaceMasterApi
      .index({});

    const selectItemList: Array<SelectOption> = [];
    for (let i = 0; i < result.spaces.length; i +=1 ) {
      selectItemList.push({
        value: result.spaces[i].id,
        label: result.spaces[i].name
      });
    }
    setVHSpaceSetLists(selectItemList);
  }

  const loadRoomVhDefMaster = async () => {
    const result = await roomVhDefMasterApi
      .index({});
    const selectItemList: Array<SelectOption> = [];
    for (let i = 0; i < result.vhDefines.length; i +=1 ) {
      selectItemList.push({
        value: result.vhDefines[i].id,
        label: result.vhDefines[i].name
      });
    }

    setVHDefinesLists(selectItemList);
  }

  const loadMenusetMaster = async () => {
    const result = await roomMenusetApi
      .index(
        undefined,
        0,
        undefined,
        undefined,
        {}
      );
    const selectItemList: Array<SelectOption> = [];
    for (let i = 0; i < result.menuSetList.length; i +=1 ) {
      selectItemList.push({
        value: result.menuSetList[i].id,
        label: result.menuSetList[i].name
      });
    }
    setMenusetLists(selectItemList);
  }

  const handleSave = async () => {
    withLoading(async () => {
      setApiClientError(null);
      await handleSubmit(async (data: any) => {
        try {
          const secondarySalesmansUserIdList: number[] = [];
          for (let i = 0; i < data.secondarySalesmans.length; i += 1) {
            secondarySalesmansUserIdList.push(data.secondarySalesmans[i].userId);
          }

          await roomApi.create({
            roomName: data.roomName,
            content: data.content,
            adminInfo: data.adminInfo,
            meetingUrl: data.meetingUrl,
            enabledFlg: data.enabled,
            vhSpaceSetId: data.vhSpaceSetId,
            vhDefSetId: data.vhDefinesId,
            menuSetId: data.menuSetId,
            ownerUserId: data.ownerUserId,
            primarySalesmanUserId: data.primarySalesmanUserId,
            secondarySalesmansUserId: secondarySalesmansUserIdList,
          }, {})

          await success(`ROOM名：${data.roomName} を登録しました。`);
          goto('roomList');

        } catch (e) {
          await logError(
            location.pathname,
            'ROOMの登録に失敗しました。',
            e
          );

          if (e instanceof ApiClientError) {
            setApiClientError(e);
            return;
          }

          goto('internalServerError');
        }
      })();
    });
  };

  const handlePreviousClick = () => {
    handleSubmit(() => {
      if (currentStep > 1) {
        setCurrentStep(currentStep - 1);
      }
    })();
  }

  const handleNextClick = () => {
    handleSubmit(() => {
      if (currentStep < TOTAL_STEP_COUNT) {
        setCurrentStep(currentStep + 1);
      }
    })();
  }

  const handle3DModelPreviewClick = () => {
    const vhSpaceSetId = getValues('vhSpaceSetId');
    if (vhSpaceSetId === undefined) {
      warn('3Dモデルを選択してください');
      return;
    }

    const previewUrl = config.room.page.preview.url;
    const queryParams = {
      mainasset: config.room.file.threeDModel.url?.replace('<id>', vhSpaceSetId),
    }

    window.open(`${previewUrl}?${qs.stringify(queryParams)}`, '_blank');
  }

  const handleVhDefinePreviewClick = () => {
    const vhSpaceSetId = getValues('vhSpaceSetId');
    if (vhSpaceSetId === undefined) {
      warn('3Dモデルを選択してください');
      return;
    }

    const vhDefinesId = getValues('vhDefinesId');
    if (vhDefinesId === undefined) {
      warn('VH定義を選択してください');
      return;
    }

    const previewUrl = config.room.page.preview.url;
    const queryParams = {
      mainasset: config.room.file.threeDModel.url?.replace('<id>', vhSpaceSetId),
      chat: config.room.file.balloonChatScenario.url?.replace('<id>', vhDefinesId),
    }

    window.open(`${previewUrl}?${qs.stringify(queryParams)}`, '_blank');
  }

  React.useEffect(() => {
    const fetch = async () => {

      withLoading(async () => {
        try {
          await Promise.all( [
            loadRoomSpaceMaster(),
            loadRoomVhDefMaster(),
            loadMenusetMaster()
          ]);
        } catch(e) {
          await logError(
            location.pathname,
            'マスタの読み込みに失敗しました。',
            e
          );

          goto('internalServerError');
        }
      });
    };
    fetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSelectOwnerSubmitted = async (isChange: boolean, owner: Member | undefined) => {
    if (isChange && owner !== undefined) {
      setOwnerName(owner.userName);
      setValue("ownerUserId", owner.userId);
    }
    setShowSelectOwnerModal(false);
  };

  const onSelectPrimarySalesmanSubmitted = async (isChange: boolean, salesman: Salesman) => {
    if (isChange) {
      setSelectedSalesmanEmails((prevState) => {
        let newState = prevState;
        if (primarySalesman !== undefined) {
          newState = prevState.filter((email) => email !== primarySalesman.email)
        }

        return newState.concat([salesman.email]);
      });
      setPrimarySalesman(salesman);
      setValue("primarySalesmanUserId", salesman.userId);
    }
    setShowSelectPrimarySalesmanModal(false);
  };

  const onSelectSecondarySalesmanSubmitted = async (isChange: boolean, salesman: Salesman) => {
    if (isChange) {
      secondarySalesmanAppend(salesman);
      setSelectedSalesmanEmails((prevState) => prevState.concat([salesman.email]));
    }
    setShowSelectSecondarySalesmanModal(false);
  };

  const handleRemoveSecondarySalesman = async (index: number) => {
    setSelectedSalesmanEmails(prevState => prevState.filter((email) => email !== getValues('secondarySalesmans')[index].email));
    secondarySalesmanRemove(index);
  }

  return (
    <>
      {showSelectOwnerModal && (
        <SelectOwnerModal
          {...{
            onSubmitted: onSelectOwnerSubmitted,
          }}
        />
      )}

      {showSelectPrimarySalesmanModal && (
        <SelectSalesmanModal
          {...{
            selectedSalesmanEmail,
            onSubmitted: onSelectPrimarySalesmanSubmitted,
            setShowModal: setShowSelectPrimarySalesmanModal,
          }}
        />
      )}
      {showSelectSecondarySalesmanModal && (
        <SelectSalesmanModal
          {...{
            selectedSalesmanEmail,
            onSubmitted: onSelectSecondarySalesmanSubmitted,
            setShowModal: setShowSelectSecondarySalesmanModal,
          }}
        />
      )}

      <div className="bg-sub flex flex-col min-h-full">
        <div className="text-xl text-primary font-semibold py-6 px-8">ROOM登録</div>
        <div className="text-danger text-xs mb-4 px-8">
          <div className="mb-4 text-danger text-xs">
            { apiClientError?.message }
            { (Object.keys(errors).length > 0) && '入力エラーがあります' }
          </div>
        </div>

        <div className="mx-8">
          <StepNav
            {...{
              currentStep,
            }}
          />
        </div>
        <div className="border-r border-b border-l border-white shadow-md flex flex-col mx-8 p-2">
          <form id="roomForm">
            <div className="p-6">
              { currentStep === 1 && (
                <div id="link1">
                  { /* ROOM名 */ }
                  <div className="mb-4">
                    <label className="block" htmlFor="roomName">
                      <div className="text-sm font-semibold text-primary mb-1">
                        <span>ROOM名</span>
                        <span className="ml-2 required">必須</span>
                      </div>
                      <input
                        /* eslint-disable-next-line react/jsx-props-no-spreading */
                        {...register('roomName',
                          makeRegisterOptions('ROOM名', [
                            { type: FormValidationType.REQUIRED },
                            { type: FormValidationType.MAX_STRING_LENGTH, value: 255 },
                          ])
                        )}
                        className="text-field"
                        id="roomName"
                        type="text"
                        maxLength={255}
                      />
                      <div className="mt-1 text-danger text-xs">
                        {errors.roomName?.message}
                      </div>
                      <ValidationErrorMessages
                        {...{
                          fieldErrors: apiClientError?.errors,
                          field: 'roomName',
                        }}
                      />
                    </label>
                  </div>

                  { /* 説明 */ }
                  <div className="mb-4">
                    <label className="block" htmlFor="content">
                      <div className="text-sm font-semibold text-primary mb-1">
                        <span>説明</span>
                      </div>
                      <textarea
                        /* eslint-disable-next-line react/jsx-props-no-spreading */
                        {...register('content',
                          makeRegisterOptions('説明', [
                            { type: FormValidationType.MAX_STRING_LENGTH, value: 1024 },
                          ])
                        )}
                        className="text-field w-full"
                        id="content"
                        name="content"
                        rows={5}
                        maxLength={1024}
                      />
                      <div className="mt-1 text-danger text-xs">
                        {errors.content?.message}
                      </div>
                      <ValidationErrorMessages
                        {...{
                          fieldErrors: apiClientError?.errors,
                          field: 'content',
                        }}
                      />
                    </label>
                  </div>

                  <div className="mb-4">
                    <Controller
                      name="enabled"
                      control={control}
                      /* eslint-disable-next-line react/jsx-boolean-value */
                      defaultValue={true}
                      render={({field: { onChange, value }}) => (
                        <Switch
                          onChange={onChange}
                          checked={value}
                          className="react-switch"
                          width={70}
                          uncheckedIcon={
                            <span
                              className="inline-block align-middle font-bold text-white"
                              style={{
                                fontSize: 15,
                                paddingLeft: 3,
                              }}
                            >
                            無効
                          </span>
                          }
                          checkedIcon={
                            <span
                              className="inline-block align-middle font-bold text-white"
                              style={{
                                fontSize: 15,
                                paddingLeft: 8,
                              }}
                            >
                            有効
                          </span>
                          }
                        />)}
                    />
                  </div>

                  <div className="mb-4">
                    <label className="block" htmlFor="adminInfo">
                      <div className="text-sm font-semibold text-primary mb-1">
                        <span>運営会社・運営責任者</span>
                        <span className="ml-2 required">必須</span>
                      </div>
                      <input
                        /* eslint-disable-next-line react/jsx-props-no-spreading */
                        {...register('adminInfo',
                          makeRegisterOptions('運営会社・運営責任者', [
                            { type: FormValidationType.REQUIRED },
                            { type: FormValidationType.MAX_STRING_LENGTH, value: 255 },
                          ])
                        )}
                        className="text-field"
                        id="adminInfo"
                        type="text"
                        maxLength={255}
                      />
                      <div className="mt-1 text-danger text-xs">
                        {errors.adminInfo?.message}
                      </div>
                      <ValidationErrorMessages
                        {...{
                          fieldErrors: apiClientError?.errors,
                          field: 'adminInfo',
                        }}
                      />
                    </label>
                  </div>

                  <div className="mb-4">
                    <label className="block" htmlFor="meetingUrl">
                      <div className="text-sm font-semibold text-primary mb-1">
                        <span>会議URL</span>
                      </div>
                      <input
                        /* eslint-disable-next-line react/jsx-props-no-spreading */
                        {...register('meetingUrl',
                          makeRegisterOptions('会議URL', [
                            { type: FormValidationType.MAX_STRING_LENGTH, value: 255 },
                            { type: FormValidationType.URL, typeName: 'url' },
                          ])
                        )}
                        className="text-field"
                        id="meetingUrl"
                        type="text"
                        maxLength={255}
                      />
                      <div className="mt-1 text-danger text-xs">
                        {errors.meetingUrl?.message}
                      </div>
                      <ValidationErrorMessages
                        {...{
                          fieldErrors: apiClientError?.errors,
                          field: 'meetingUrl',
                        }}
                      />
                    </label>
                  </div>
                </div>
              )}
              { currentStep === 2 && (
                <div id="link2">
                  <div className="mb-4">
                    <div className="text-sm font-semibold text-primary mb-1">
                      <span>3Dモデル</span>
                      <span className="ml-2 required">必須</span>
                    </div>
                    <div className="flex items-center">
                      <Controller
                        name="vhSpaceSetId"
                        control={control}
                        rules={
                          makeRegisterOptions('3Dモデル', [
                            { type: FormValidationType.REQUIRED },
                          ])
                        }
                        render={({field: { onChange, value }}) => (
                          <Select
                            options={vhSpaceSetList}
                            isSearchable
                            className="w-1/2 text-sm"
                            value={vhSpaceSetList.find(c => c.value === value)}
                            onChange={(val) => {
                              if (val !== null) {
                                onChange(val.value);
                              }
                            }}
                          />
                        )}
                      />
                      <div>
                        <button
                          type="button"
                          className="btn-primary bg-blue-500 ml-1"
                          onClick={handle3DModelPreviewClick}
                        >
                          プレビュー
                        </button>
                      </div>
                    </div>
                    <div className="mt-1 text-danger text-xs">
                      {errors.vhSpaceSetId?.message}
                    </div>
                    <ValidationErrorMessages
                      {...{
                        fieldErrors: apiClientError?.errors,
                        field: 'vhSpaceSetId',
                      }}
                    />
                  </div>
                </div>
              )}
              { currentStep === 3 && (
                <div id="link3">
                  <div className="mb-4">
                    <div className="text-sm font-semibold text-primary mb-1">
                      <span>VH定義</span>
                      <span className="ml-2 required">必須</span>
                    </div>
                    <div className="flex items-center">
                      <Controller
                        name="vhDefinesId"
                        control={control}
                        rules={
                          makeRegisterOptions('VH定義', [
                            {type: FormValidationType.REQUIRED},
                          ])
                        }
                        render={({field: { onChange, value }}) => (
                          <Select
                            options={vhDefinesList}
                            isSearchable
                            className="w-1/2 text-sm"
                            value={vhDefinesList.find(c => c.value === value)}
                            onChange={(val) => {
                              if (val !== null) {
                                onChange(val.value);
                              }
                            }}
                          />
                        )}
                      />
                      <div>
                        <button
                          type="button"
                          className="btn-primary bg-blue-500 ml-1"
                          onClick={handleVhDefinePreviewClick}
                        >
                          プレビュー
                        </button>
                      </div>
                    </div>
                    <div className="mt-1 text-danger text-xs">
                      {errors.vhDefinesId?.message}
                    </div>
                    <ValidationErrorMessages
                      {...{
                        fieldErrors: apiClientError?.errors,
                        field: 'vhDefinesId',
                      }}
                    />
                  </div>
                </div>
              )}
              { currentStep === 4 && (
                <div id="link4">
                  <div className="mb-4">
                    <div className="text-sm font-semibold text-primary mb-1">
                      <span>メニューセット</span>
                      <span className="ml-2 required">必須</span>
                    </div>
                    <div className="flex items-center">
                      <Controller
                        name="menuSetId"
                        control={control}
                        rules={
                          makeRegisterOptions('メニューセット', [
                            {type: FormValidationType.REQUIRED},
                          ])
                        }
                        render={({field: { onChange, value }}) => (
                          <Select
                            options={menusetList}
                            isSearchable
                            className="w-1/2 text-sm"
                            value={menusetList.find(c => c.value === value)}
                            onChange={(val) => {
                              if (val !== null) {
                                onChange(val.value);
                              }
                            }}
                          />
                        )}
                      />
                    </div>
                    <div className="mt-1 text-danger text-xs">
                      {errors.menuSetId?.message}
                    </div>
                    <ValidationErrorMessages
                      {...{
                        fieldErrors: apiClientError?.errors,
                        field: 'menuSetId',
                      }}
                    />
                  </div>
                </div>
              )}
              { currentStep === 5 && (
                <div id="link5" className="flex">
                  { /* オーナー関連入力 */ }
                  <div className="w-full">
                    { /* オーナー */ }
                    <div className="mb-4">
                      {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                      <label className="block">
                        <div className="text-sm font-semibold text-primary mb-1">
                          <span>オーナー</span>
                          <span className="ml-2 required">必須</span>
                        </div>
                        <div className="p-2">
                          <div className="flex items-center">
                            { ownerName !== undefined && (
                              <div className="mr-2">{ ownerName }</div>
                            )}
                            <button
                              type="button"
                              className="btn-primary"
                              onClick={() => setShowSelectOwnerModal(true)}
                            >
                              { ownerName === undefined  ? '追加' : '変更' }
                            </button>
                          </div>
                          <input
                            /* eslint-disable-next-line react/jsx-props-no-spreading */
                            {...register('ownerUserId',
                              makeRegisterOptions('オーナー', [
                                {type: FormValidationType.REQUIRED},
                              ])
                            )}
                            id="ownerUserId"
                            type="hidden"
                          />
                          <div className="mt-1 text-danger text-xs">
                            {errors.ownerUserId?.message}
                          </div>
                          <ValidationErrorMessages
                            {...{
                              fieldErrors: apiClientError?.errors,
                              field: 'ownerUserId',
                            }}
                          />
                        </div>
                      </label>
                    </div>
                  </div>

                  { /* 営業関連入力 */ }
                  <div className="w-full">
                    { /* 主営業担当者 */ }
                    <div className="mb-4">
                      {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                      <label className="block">
                        <div className="text-sm font-semibold text-primary mb-1">
                          <span>主営業担当者</span>
                          <span className="ml-2 required">必須</span>
                        </div>
                        <div className="p-2">
                          <div className="flex items-center">
                            { primarySalesman !== undefined && (
                              <div className="mr-2">{ primarySalesman?.userName }</div>
                            )}
                            <button
                              type="button"
                              className="btn-primary"
                              onClick={() => setShowSelectPrimarySalesmanModal(true)}
                            >
                              { primarySalesman === undefined ? '追加' : '変更' }
                            </button>
                          </div>

                          <input
                            /* eslint-disable-next-line react/jsx-props-no-spreading */
                            {...register('primarySalesmanUserId',
                              makeRegisterOptions('主営業担当', [
                                {type: FormValidationType.REQUIRED},
                              ])
                            )}
                            id="primarySalesmanUserId"
                            type="hidden"
                          />
                          <div className="mt-1 text-danger text-xs">
                            {errors.primarySalesmanUserId?.message}
                          </div>
                          <ValidationErrorMessages
                            {...{
                              fieldErrors: apiClientError?.errors,
                              field: 'primarySalesmanUserId',
                            }}
                          />
                        </div>
                      </label>
                    </div>

                    { /* 副営業担当者 */ }
                    { primarySalesman !== undefined && (
                      <div className="mb-4">
                        {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                        <label className="block">
                          <div className="flex justify-between items-center mb-1">
                            <div className="text-sm font-semibold text-primary">
                              <span>副営業担当者</span>
                            </div>
                            <button
                              type="button"
                              className="btn-primary"
                              onClick={() => {
                                setShowSelectSecondarySalesmanModal(true)}
                              }
                            >
                              追加
                            </button>
                          </div>
                        </label>
                        <SecondarySalesmanList
                          {...{
                            tableData: getValues('secondarySalesmans'),
                            removeSecondarySalesman : handleRemoveSecondarySalesman,
                          }}
                        />
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </form>
        </div>

        <div className="sticky flex bottom-0 bg-sub mt-auto py-4 px-8 border-t border-primary">
          <button
            type="button"
            className="btn-sub"
            onClick={() => {
              goto('roomList');
            }}
          >
            キャンセル
          </button>

          <div className="ml-auto">
            { currentStep > 1 && (
              <button
                type="button"
                className="btn-primary mr-2"
                onClick={handlePreviousClick}
              >
                前へ
              </button>
            ) }
            { currentStep < TOTAL_STEP_COUNT && (
              <button
                type="button"
                className="btn-primary"
                onClick={handleNextClick}
              >
                次へ
              </button>
            ) }
            { currentStep === TOTAL_STEP_COUNT && (
              <button
                type="button"
                className="btn-primary"
                form="roomForm"
                onClick={handleSave}
              >
                保存
              </button>
            ) }
          </div>
        </div>
      </div>
    </>
  );
};
